// Voice answer parsers for colors and numbers in 6 languages.
// All inputs are normalized (lowercase, diacritics stripped).

// Map characters that NFD doesn't decompose (e.g. Polish "ł", German "ß").
const MANUAL_MAP = {
  ł: "l",
  Ł: "l",
  ß: "ss",
  œ: "oe",
  Œ: "oe",
  æ: "ae",
  Æ: "ae",
  ø: "o",
  Ø: "o",
  đ: "d",
  Đ: "d",
  ı: "i",
};

export function normalize(text) {
  let s = text.toString().toLowerCase();
  // Manual replacements first
  s = s.replace(/[łŁßœŒæÆøØđĐı]/g, (ch) => MANUAL_MAP[ch] || ch);
  // Strip remaining combining diacritics via NFD
  s = s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-z0-9а-яёїієґ'\s]/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
  return s;
}

// ---------- Numbers 0–20 ----------
const NUMBER_DICTS = {
  pl: {
    zero: 0,
    jeden: 1,
    jedna: 1,
    jedno: 1,
    dwa: 2,
    dwie: 2,
    trzy: 3,
    cztery: 4,
    piec: 5,
    szesc: 6,
    siedem: 7,
    osiem: 8,
    dziewiec: 9,
    dziesiec: 10,
    jedenascie: 11,
    dwanascie: 12,
    trzynascie: 13,
    czternascie: 14,
    pietnascie: 15,
    szesnascie: 16,
    siedemnascie: 17,
    osiemnascie: 18,
    dziewietnascie: 19,
    dwadziescia: 20,
  },
  en: {
    zero: 0,
    one: 1,
    two: 2,
    three: 3,
    four: 4,
    five: 5,
    six: 6,
    seven: 7,
    eight: 8,
    nine: 9,
    ten: 10,
    eleven: 11,
    twelve: 12,
    thirteen: 13,
    fourteen: 14,
    fifteen: 15,
    sixteen: 16,
    seventeen: 17,
    eighteen: 18,
    nineteen: 19,
    twenty: 20,
  },
  de: {
    null: 0,
    eins: 1,
    ein: 1,
    eine: 1,
    zwei: 2,
    drei: 3,
    vier: 4,
    funf: 5,
    sechs: 6,
    sieben: 7,
    acht: 8,
    neun: 9,
    zehn: 10,
    elf: 11,
    zwolf: 12,
    dreizehn: 13,
    vierzehn: 14,
    funfzehn: 15,
    sechzehn: 16,
    siebzehn: 17,
    achtzehn: 18,
    neunzehn: 19,
    zwanzig: 20,
  },
  es: {
    cero: 0,
    uno: 1,
    una: 1,
    dos: 2,
    tres: 3,
    cuatro: 4,
    cinco: 5,
    seis: 6,
    siete: 7,
    ocho: 8,
    nueve: 9,
    diez: 10,
    once: 11,
    doce: 12,
    trece: 13,
    catorce: 14,
    quince: 15,
    dieciseis: 16,
    diecisiete: 17,
    dieciocho: 18,
    diecinueve: 19,
    veinte: 20,
  },
  fr: {
    zero: 0,
    un: 1,
    une: 1,
    deux: 2,
    trois: 3,
    quatre: 4,
    cinq: 5,
    six: 6,
    sept: 7,
    huit: 8,
    neuf: 9,
    dix: 10,
    onze: 11,
    douze: 12,
    treize: 13,
    quatorze: 14,
    quinze: 15,
    seize: 16,
    "dix-sept": 17,
    "dix-huit": 18,
    "dix-neuf": 19,
    vingt: 20,
  },
  uk: {
    нуль: 0,
    один: 1,
    одна: 1,
    одне: 1,
    два: 2,
    дві: 2,
    три: 3,
    чотири: 4,
    "п'ять": 5,
    пять: 5,
    шість: 6,
    шiсть: 6,
    сім: 7,
    сiм: 7,
    вісім: 8,
    вiсiм: 8,
    "дев'ять": 9,
    девять: 9,
    десять: 10,
    одинадцять: 11,
    дванадцять: 12,
    тринадцять: 13,
    чотирнадцять: 14,
    "п'ятнадцять": 15,
    пятнадцять: 15,
    шістнадцять: 16,
    шiстнадцять: 16,
    сімнадцять: 17,
    сiмнадцять: 17,
    вісімнадцять: 18,
    вiсiмнадцять: 18,
    "дев'ятнадцять": 19,
    девятнадцять: 19,
    двадцять: 20,
  },
};

/**
 * Parse ALL numbers from the transcript. Recognizes raw digits ("10") and
 * number words in the given language. Returns an array of integers in the
 * order they appeared. Empty array when nothing matched.
 */
export function parseNumbers(text, lang) {
  const norm = normalize(text);
  const found = [];
  // Word-based first (in transcript order, preserving position)
  const dict = NUMBER_DICTS[lang] || NUMBER_DICTS.en;
  const tokens = norm.split(/\s+/).filter(Boolean);
  tokens.forEach((token, idx) => {
    if (dict[token] !== undefined) {
      found.push({ value: dict[token], idx });
    }
    // Token might contain a raw digit like "10"
    const m = token.match(/\d{1,3}/);
    if (m) {
      const n = parseInt(m[0], 10);
      if (!Number.isNaN(n)) found.push({ value: n, idx });
    }
  });
  return found.map((f) => f.value);
}

/**
 * Convenience: returns the LAST number heard, or null when none found.
 * Last is preferred because, when a player speaks an equation aloud
 * ("five plus five equals ten"), the final number is their answer.
 */
export function parseNumber(text, lang) {
  const all = parseNumbers(text, lang);
  return all.length ? all[all.length - 1] : null;
}

// ---------- Colors ----------
const COLOR_DICTS = {
  pl: {
    red: ["czerwony", "czerwona", "czerwone", "czerwon", "red"],
    blue: ["niebieski", "niebieska", "niebieskie", "niebiesk", "blue"],
    yellow: ["zolty", "zolta", "zolte", "zolt", "yellow"],
    green: ["zielony", "zielona", "zielone", "zielon", "green"],
  },
  en: {
    red: ["red", "scarlet"],
    blue: ["blue"],
    yellow: ["yellow"],
    green: ["green"],
  },
  de: {
    red: ["rot", "rote", "roter", "rotes"],
    blue: ["blau", "blaue", "blauer"],
    yellow: ["gelb", "gelbe", "gelber"],
    green: ["grun", "grune", "gruner"],
  },
  es: {
    red: ["rojo", "roja"],
    blue: ["azul"],
    yellow: ["amarillo", "amarilla"],
    green: ["verde"],
  },
  fr: {
    red: ["rouge"],
    blue: ["bleu", "bleue"],
    yellow: ["jaune"],
    green: ["vert", "verte"],
  },
  uk: {
    red: ["червоний", "червона", "червоне"],
    blue: ["синій", "синiй", "синя", "синє"],
    yellow: ["жовтий", "жовта", "жовте"],
    green: ["зелений", "зелена", "зелене"],
  },
};

/**
 * Returns an array of all color names mentioned in the transcript.
 * One of: "red" | "blue" | "yellow" | "green".
 */
export function parseColors(text, lang) {
  const norm = normalize(text);
  const dict = COLOR_DICTS[lang] || COLOR_DICTS.en;
  const found = [];
  const words = norm.split(/\s+/).filter(Boolean);
  for (const w of words) {
    for (const [colorName, variants] of Object.entries(dict)) {
      for (const v of variants) {
        const nv = normalize(v);
        if (w === nv || w.startsWith(nv)) {
          found.push(colorName);
          break;
        }
      }
    }
  }
  return found;
}

/**
 * Convenience: returns the LAST color heard, or null if none.
 */
export function parseColor(text, lang) {
  const all = parseColors(text, lang);
  return all.length ? all[all.length - 1] : null;
}
